import type { HTMLAttributes, InputHTMLAttributes, LabelHTMLAttributes, Ref } from "react";

import { cn } from "../lib/cn";

/**
 * Labels sit above their input and use the uppercase label style, which is how the
 * design system separates metadata (Cage ID, Genotype) from content.
 */
export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn("text-node text-on-surface-body", className)}
      {...props}
    />
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
}

/**
 * A 52px pill on a near-white ground, borderless at rest.
 *
 * Focus is a 2.75px inset ring in the accent mark rather than an outline: an outline on
 * a fully-round field at this radius reads as a second, slightly wrong-sized pill around
 * the first. Inset, it reads as the field itself lighting up.
 */
export function Input({ className, ref, ...props }: InputProps) {
  return (
    <input
      ref={ref}
      className={cn(
        "h-13.5 w-full rounded-control px-5",
        "bg-surface-container-lowest text-field text-on-surface",
        "placeholder:text-on-surface-variant",
        "outline-none transition-shadow",
        "shadow-card",
        "focus:shadow-[inset_0_0_0_2.75px_var(--color-action)]",
        "disabled:opacity-45",
        "aria-invalid:shadow-[inset_0_0_0_2px_var(--color-error)]",
        className,
      )}
      {...props}
    />
  );
}

/** Label, control and error message as one unit, on the 8px rhythm. */
export function Field({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col gap-1.5", className)} {...props} />;
}

export function FieldError({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p role="alert" className={cn("px-md text-caption text-error", className)} {...props} />;
}

export function FieldHint({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn("text-note text-on-surface-variant", className)} {...props} />
  );
}
